import React, { useState, useEffect, useRef } from 'react';
import { supabaseService } from '../supabaseService';
import { Comment, Profile, cleanAuthorName } from '../types';
import { 
  Send, CornerDownRight, MessageCircle, Star, 
  Bold, Italic, Quote, Code, AtSign, Heart, 
  ChevronDown, ChevronUp, Sparkles, Smile, Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';

const MAX_CHARS = 500;

// Rich text inline token renderer
const renderInlineTokens = (text: string) => {
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|@\w+)/g;
  const parts = text.split(regex);

  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={i} className="font-bold text-slate-900 dark:text-white">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={i} className="italic text-slate-800 dark:text-slate-200">{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={i} className="bg-slate-200/80 dark:bg-slate-700/80 text-indigo-600 dark:text-indigo-300 px-1.5 py-0.5 rounded text-[11px] font-mono">
          {part.slice(1, -1)}
        </code>
      );
    }
    if (part.startsWith('@') && part.length > 1) {
      const username = part.slice(1);
      return (
        <Link
          key={i}
          to={`/author/${username}`}
          className="font-bold text-indigo-600 dark:text-indigo-400 hover:underline px-1 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 rounded text-xs"
        >
          {part}
        </Link>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const RichCommentText: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split('\n');
  return (
    <div className="space-y-1 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
      {lines.map((line, idx) => {
        let isQuote = false;
        let lineText = line;
        if (lineText.startsWith('> ')) {
          isQuote = true;
          lineText = lineText.slice(2);
        }

        if (isQuote) {
          return (
            <blockquote 
              key={idx} 
              className="border-l-2 border-indigo-500 dark:border-indigo-400 pl-3 py-1 my-1 text-xs italic bg-indigo-50/60 dark:bg-indigo-950/40 rounded-r text-slate-600 dark:text-slate-300"
            >
              {renderInlineTokens(lineText)}
            </blockquote>
          );
        }

        return (
          <div key={idx}>
            {renderInlineTokens(lineText)}
          </div>
        );
      })}
    </div>
  );
};

interface CommentSectionProps {
  resourceId: string;
  resourceType: 'post' | 'book' | 'chapter';
  maxHeight?: string;
  onCommentAdded?: () => void;
}

const CommentSection: React.FC<CommentSectionProps> = ({ resourceId, resourceType, maxHeight = "400px", onCommentAdded }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [rating, setRating] = useState<number>(0);
  const [replyingTo, setReplyingTo] = useState<{ comment: Comment; parentId: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState<Record<string, boolean>>({});
  const [likedComments, setLikedComments] = useState<Record<string, boolean>>({});
  const [commentLikesCount, setCommentLikesCount] = useState<Record<string, number>>({});
  
  const navigate = useNavigate();

  // Mention state
  const [mentionQuery, setMentionQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Profile[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [currentUser, setCurrentUser] = useState<Profile | null>(null);

  useEffect(() => {
    supabaseService.getProfile().then(setCurrentUser);
    loadComments();
  }, [resourceId]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const data = await supabaseService.getComments(resourceId);
      const topLevel = data.filter(c => !c.parent_id);
      const replies = data.filter(c => c.parent_id);
      const threaded = topLevel.map(parent => ({
        ...parent,
        replies: replies.filter(r => r.parent_id === parent.id).sort((a,b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      }));
      
      // Auto-expand threads with <= 2 replies
      const initialExpanded: Record<string, boolean> = {};
      threaded.forEach(t => {
        if ((t.replies?.length || 0) <= 2) {
          initialExpanded[t.id] = true;
        }
      });
      setExpandedThreads(initialExpanded);
      setComments(threaded);
    } catch (err) { 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setNewComment(val);

    const lastWord = val.split(/\s+/).pop() || '';
    if (lastWord.startsWith('@') && lastWord.length > 1) {
      setMentionQuery(lastWord.slice(1));
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    if (showSuggestions && mentionQuery.trim()) {
      supabaseService.getMentionSuggestions(mentionQuery).then(setSuggestions);
    }
  }, [mentionQuery, showSuggestions]);

  const insertMention = (username: string) => {
    const words = newComment.split(' ');
    words.pop();
    setNewComment([...words, `@${username}`, ''].join(' '));
    setShowSuggestions(false);
    textareaRef.current?.focus();
  };

  const applyFormat = (prefix: string, suffix: string = '') => {
    if (!textareaRef.current) return;
    const el = textareaRef.current;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const val = newComment;

    const selectedText = val.substring(start, end);
    const replacement = selectedText ? `${prefix}${selectedText}${suffix}` : `${prefix}${suffix}`;
    
    const updated = val.substring(0, start) + replacement + val.substring(end);
    setNewComment(updated);

    setTimeout(() => {
      el.focus();
      const newCursorPos = selectedText ? start + replacement.length : start + prefix.length;
      el.setSelectionRange(newCursorPos, newCursorPos);
    }, 10);
  };

  const toggleThread = (commentId: string) => {
    setExpandedThreads(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const toggleCommentLike = (commentId: string) => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    setLikedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
    setCommentLikesCount(prev => ({
      ...prev,
      [commentId]: (prev[commentId] || 0) + (likedComments[commentId] ? -1 : 1)
    }));
  };

  const handleStartReply = (targetComment: Comment, parentId: string) => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    setReplyingTo({ comment: targetComment, parentId });
    // Pre-fill mention if replying to child reply or specific user
    const handleName = targetComment.user_name.includes('@') 
      ? targetComment.user_name.split('@')[1]?.split(' ')[0] 
      : targetComment.user_name.replace(/\s+/g, '');

    if (handleName && !newComment.includes(`@${handleName}`)) {
      setNewComment(prev => `@${handleName} ${prev}`);
    }
    textareaRef.current?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newComment.trim();
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    if (!trimmed || isSubmitting || trimmed.length > MAX_CHARS) return;

    setIsSubmitting(true);
    const authorName = cleanAuthorName(currentUser.display_name || currentUser.full_name);

    const commentPayload: any = {
      user_id: currentUser.id,
      user_name: authorName,
      user_avatar: currentUser.avatar_url,
      content: trimmed,
    };

    let optimisticRating = 0;
    if (resourceType === 'book' && !replyingTo && rating > 0) {
      commentPayload.content = `[Rating: ${rating}/5] ${trimmed}`;
      optimisticRating = rating;
    }

    if (resourceType === 'post') commentPayload.post_id = resourceId;
    if (resourceType === 'book') commentPayload.book_id = resourceId;
    if (resourceType === 'chapter') commentPayload.chapter_id = resourceId;
    
    if (replyingTo) {
      commentPayload.parent_id = replyingTo.parentId;
    }

    // Optimistic update
    const tempId = Math.random().toString(36).substr(2, 9);
    const optimisticComment: Comment = {
      id: tempId,
      created_at: new Date().toISOString(),
      rating: optimisticRating > 0 ? optimisticRating : undefined,
      ...commentPayload
    };

    const prevComments = [...comments];
    if (replyingTo) {
      setComments(prev => prev.map(c => c.id === replyingTo.parentId ? { ...c, replies: [...(c.replies || []), optimisticComment] } : c));
      setExpandedThreads(prev => ({ ...prev, [replyingTo.parentId]: true }));
    } else {
      setComments(prev => [optimisticComment, ...prev]);
    }

    setNewComment('');
    setRating(0);
    setReplyingTo(null);
    if (onCommentAdded) onCommentAdded();

    try {
      const savedComment = await supabaseService.createComment(commentPayload);
      if (replyingTo) {
        setComments(prev => prev.map(c => c.id === replyingTo.parentId ? { ...c, replies: (c.replies || []).map(r => r.id === tempId ? savedComment : r) } : c));
      } else {
        setComments(prev => prev.map(c => c.id === tempId ? savedComment : c));
      }
    } catch (err) { 
      console.error(err); 
      setComments(prevComments);
      setNewComment(optimisticComment.content);
    } finally { 
      setIsSubmitting(false); 
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      setComments(prev => {
        const filterTree = (list: Comment[]): Comment[] => {
          return list
            .filter(c => c.id !== commentId)
            .map(c => ({
              ...c,
              replies: c.replies ? filterTree(c.replies) : []
            }));
        };
        return filterTree(prev);
      });
      await supabaseService.deleteComment(commentId);
      if (onCommentAdded) onCommentAdded();
    } catch (err) {
      console.warn("Failed to delete comment:", err);
    }
  };

  const charCount = newComment.length;
  const isOverLimit = charCount > MAX_CHARS;
  const progressPct = Math.min(100, (charCount / MAX_CHARS) * 100);

  return (
    <div className="flex flex-col h-full relative">
      {/* Comments List */}
      <div className="flex-1 overflow-y-auto no-scrollbar space-y-4 p-1" style={{ maxHeight }}>
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-indigo-600 dark:border-indigo-400 rounded-full border-t-transparent animate-spin"/>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 text-slate-400 dark:text-slate-500">
            <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm font-medium">Be the first to share a thoughtful whisper.</p>
          </div>
        ) : (
          comments.map(comment => {
            let displayContent = comment.content;
            let displayRating = comment.rating;
            
            const ratingMatch = displayContent.match(/^\[Rating: ([1-5])\/5\] /);
            if (ratingMatch) {
              displayRating = parseInt(ratingMatch[1], 10);
              displayContent = displayContent.replace(/^\[Rating: [1-5]\/5\] /, '');
            }

            const isExpanded = expandedThreads[comment.id] ?? true;
            const replies = comment.replies || [];
            const hasReplies = replies.length > 0;
            const isLiked = likedComments[comment.id];
            const likes = (commentLikesCount[comment.id] || 0);

            return (
              <div key={comment.id} className="group space-y-2 mb-6">
                <div className="flex gap-3">
                  <img 
                    src={comment.user_avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80'} 
                    referrerPolicy="no-referrer"
                    className="w-10 h-10 rounded-full flex-shrink-0 object-cover cursor-pointer hover:ring-2 hover:ring-indigo-500 transition-all" 
                    onClick={() => navigate(currentUser?.id === comment.user_id ? '/profile' : `/author/${comment.user_id}`)} 
                  />
                  <div className="flex-1 min-w-0">
                    <div className="p-1">
                      <div className="flex justify-between items-center mb-1.5">
                        <p 
                          className="text-sm font-bold text-slate-900 dark:text-white truncate cursor-pointer hover:text-indigo-600 transition-colors"
                          onClick={() => navigate(currentUser?.id === comment.user_id ? '/profile' : `/author/${comment.user_id}`)}
                        >
                          {cleanAuthorName(comment.user_name)}
                        </p>
                        {displayRating && (
                          <div className="flex gap-0.5 bg-amber-50 dark:bg-amber-950/40 px-1.5 py-0.5 rounded-md border border-amber-200/50 dark:border-amber-800/50">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star key={star} size={10} className={star <= displayRating! ? "text-amber-400 fill-amber-400" : "text-slate-300 dark:text-slate-600"} />
                            ))}
                          </div>
                        )}
                      </div>

                      <RichCommentText content={displayContent} />
                    </div>

                    <div className="flex items-center justify-between mt-1.5 px-2 text-xs text-slate-400">
                      <div className="flex items-center gap-4">
                        <span>{new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        <button 
                          onClick={() => handleStartReply(comment, comment.id)} 
                          className="font-bold text-slate-600 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 transition-colors"
                        >
                          Reply
                        </button>
                        <button 
                          onClick={() => handleDeleteComment(comment.id)} 
                          className="text-slate-400 hover:text-rose-500 transition-colors flex items-center gap-1"
                          title="Delete comment"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>

                      <button 
                        onClick={() => toggleCommentLike(comment.id)}
                        className={`flex items-center gap-1 transition-colors ${isLiked ? 'text-rose-500 font-bold' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                      >
                        <Heart size={12} className={isLiked ? "fill-rose-500" : ""} />
                        {likes > 0 && <span>{likes}</span>}
                      </button>
                    </div>

                    {/* Replies Collapsible */}
                    {hasReplies && (
                      <div className="mt-2 ml-2">
                        <button 
                          onClick={() => toggleThread(comment.id)}
                          className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline py-1"
                        >
                          <CornerDownRight size={12} />
                          <span>{isExpanded ? 'Hide replies' : `Show ${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`}</span>
                          {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>

                        {isExpanded && (
                          <div className="mt-4 space-y-6 pl-4 border-l-2 border-slate-100 dark:border-slate-800">
                            {replies.map(reply => {
                              let replyContent = reply.content;
                              const replyRatingMatch = replyContent.match(/^\[Rating: ([1-5])\/5\] /);
                              if (replyRatingMatch) {
                                replyContent = replyContent.replace(/^\[Rating: [1-5]\/5\] /, '');
                              }
                              const isReplyLiked = likedComments[reply.id];
                              const replyLikes = (commentLikesCount[reply.id] || 0);

                              return (
                                <div key={reply.id} className="flex gap-2">
                                  <img 
                                    src={reply.user_avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80'} 
                                    referrerPolicy="no-referrer"
                                    className="w-8 h-8 rounded-full flex-shrink-0 object-cover mt-1" 
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="p-1">
                                      <p className="text-sm font-bold text-slate-900 dark:text-white mb-1">{cleanAuthorName(reply.user_name)}</p>
                                      <RichCommentText content={replyContent} />
                                    </div>
                                    <div className="flex items-center justify-between mt-1 px-1 text-xs text-slate-400">
                                      <div className="flex items-center gap-3">
                                        <span>{new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        <button 
                                          onClick={() => handleStartReply(reply, comment.id)} 
                                          className="font-bold text-slate-500 hover:text-indigo-600 dark:text-slate-400"
                                        >
                                          Reply
                                        </button>
                                        <button 
                                          onClick={() => handleDeleteComment(reply.id)} 
                                          className="text-slate-400 hover:text-rose-500 transition-colors flex items-center gap-1"
                                          title="Delete reply"
                                        >
                                          <Trash2 size={11} />
                                        </button>
                                      </div>
                                      <button 
                                        onClick={() => toggleCommentLike(reply.id)}
                                        className={`flex items-center gap-1 transition-colors ${isReplyLiked ? 'text-rose-500 font-bold' : 'text-slate-400 hover:text-slate-600'}`}
                                      >
                                        <Heart size={10} className={isReplyLiked ? "fill-rose-500" : ""} />
                                        {replyLikes > 0 && <span>{replyLikes}</span>}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Write Comment Box */}
      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 sticky bottom-0 z-10">
        {/* Mention Auto-complete */}
        <AnimatePresence>
          {showSuggestions && suggestions.length > 0 && (
            <motion.div 
              initial={{ y: 10, opacity: 0 }} 
              animate={{ y: 0, opacity: 1 }} 
              exit={{ y: 10, opacity: 0 }} 
              className="absolute bottom-full left-0 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl mb-2 shadow-2xl overflow-hidden z-30"
            >
              <div className="p-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center justify-between">
                <span>Mention Author</span>
                <AtSign size={12} />
              </div>
              {suggestions.map(s => (
                <button 
                  key={s.id} 
                  type="button"
                  onClick={() => insertMention(s.display_name)} 
                  className="w-full p-2.5 flex items-center gap-3 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition-colors border-b border-slate-50 dark:border-slate-700/50 last:border-0 text-left"
                >
                  <img 
                    src={s.avatar_url || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80'} 
                    referrerPolicy="no-referrer"
                    className="w-6 h-6 rounded-full object-cover" 
                  />
                  <div>
                    <span className="font-bold text-xs text-slate-800 dark:text-slate-100">@{s.display_name}</span>
                    {s.full_name && <span className="text-[10px] text-slate-400 ml-2">({s.full_name})</span>}
                  </div>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Replying Banner */}
        {replyingTo && (
          <div className="flex items-center justify-between bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800/50 px-3 py-1.5 rounded-xl mb-2 text-xs">
            <span className="text-indigo-700 dark:text-indigo-300 font-semibold flex items-center gap-1.5">
              <CornerDownRight size={12} />
              Replying to <strong className="font-bold">{replyingTo.comment.user_name}</strong>
            </span>
            <button 
              onClick={() => setReplyingTo(null)} 
              className="text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-200 text-base leading-none font-bold px-1"
            >
              &times;
            </button>
          </div>
        )}

        {/* Rating Selector for Books */}
        {resourceType === 'book' && !replyingTo && (
          <div className="flex items-center justify-between mb-2 px-2 py-1 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-700/50">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
              <Sparkles size={12} className="text-amber-500" />
              Your Rating:
            </span>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(star => (
                <button 
                  key={star} 
                  type="button" 
                  onClick={() => setRating(star)}
                  className="focus:outline-none p-0.5 hover:scale-110 transition-transform"
                >
                  <Star size={16} className={star <= rating ? "text-amber-400 fill-amber-400" : "text-slate-300 dark:text-slate-600"} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Rich Text Controls & Textarea (Members Only) */}
        {!currentUser ? (
          <div className="p-4 bg-slate-50 dark:bg-slate-800/80 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 text-center my-2">
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-3">
              Join Literally to comment, rate works, and converse with authors.
            </p>
            <button
              type="button"
              onClick={() => navigate('/auth')}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-600/20 active:scale-95 transition-all inline-flex items-center gap-2"
            >
              <Send size={13} />
              <span>Sign In / Create Account to Comment</span>
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="bg-slate-50 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-2xl p-2 focus-within:ring-2 focus-within:ring-indigo-500 transition-all">
              {/* Formatting Toolbar */}
              <div className="flex items-center justify-between border-b border-slate-200/60 dark:border-slate-700/60 pb-1.5 mb-1.5 px-1 text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => applyFormat('**', '**')}
                    title="Bold (**text**)"
                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                  >
                    <Bold size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('*', '*')}
                    title="Italic (*text*)"
                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                  >
                    <Italic size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('> ')}
                    title="Blockquote (> quote)"
                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                  >
                    <Quote size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('`', '`')}
                    title="Inline Code (`code`)"
                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                  >
                    <Code size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('@')}
                    title="Mention (@author)"
                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                  >
                    <AtSign size={13} />
                  </button>
                </div>

                {/* Character Limit Indicator */}
                <div className="flex items-center gap-1.5">
                  <div className="relative w-4 h-4 flex items-center justify-center">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                      <path
                        className="text-slate-200 dark:text-slate-700"
                        strokeWidth="4"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path
                        className={isOverLimit ? "text-rose-500" : progressPct > 80 ? "text-amber-500" : "text-indigo-600 dark:text-indigo-400"}
                        strokeDasharray={`${progressPct}, 100`}
                        strokeWidth="4"
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                  </div>
                  <span className={`text-[10px] font-mono font-bold ${isOverLimit ? 'text-rose-500' : 'text-slate-400'}`}>
                    {charCount}/{MAX_CHARS}
                  </span>
                </div>
              </div>

              <textarea 
                ref={textareaRef}
                rows={2}
                value={newComment}
                onChange={handleTextareaChange}
                placeholder={replyingTo ? "Write a thoughtful reply..." : "Whisper a thought or critique..."}
                className="w-full bg-transparent border-none p-1 text-sm focus:outline-none resize-none dark:text-white placeholder:text-slate-400"
              />

              <div className="flex justify-end pt-1">
                <button 
                  type="submit" 
                  disabled={!newComment.trim() || isSubmitting || isOverLimit} 
                  className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm active:scale-95"
                >
                  <span>{isSubmitting ? "Sending..." : "Publish"}</span>
                  <Send size={12} />
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CommentSection;
